import { useMemo, useState } from 'react'
import {
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    List,
    ListItem,
    ListItemText,
    MenuItem,
    TextField,
    Typography,
} from '@mui/material'
import { Link, useLocation, useSearchParams } from 'react-router-dom'
import { DASHBOARD_AUTH_PATHS } from '@yourownmetric/shared'

import { useAuthorizeConnectMutation, useGetConnectRequestQuery } from '@/apis/connectApi'
import { useCreateProjectMutation, useListProjectsQuery } from '@/apis/projectsApi'
import { v2ErrorMessage } from '@/apis/v2Mappers'
import { useAppSelector } from '@/app/hooks'
import { GoogleSignInButton } from '@/modules/auth/GoogleSignInButton'

import {
    cancelUrl,
    isSubTenantId,
    parseConnectParams,
    suggestedDomain,
    suggestedSubTenant,
    type ConnectParams,
} from './connectModel'

const NEW_PROJECT = '__new__'
const column = { display: 'flex', flexDirection: 'column', gap: 2 } as const

function Problem({ title, children }: { title: string; children: string }) {
    return (
        <Box sx={{ ...column, textAlign: 'center' }}>
            <Typography variant="h5">{title}</Typography>
            <Typography color="text.secondary">{children}</Typography>
        </Box>
    )
}

/**
 * `/app/connect?platform=&store=&return=&state=&code_challenge=&code_challenge_method=S256`:
 * a marketplace app sends the merchant here to connect their store without pasting keys.
 * Sign in or sign up, pick or create the project, consent, then go back to the app with a
 * one-time code (docs/integrations/connect.md).
 */
export function ConnectPage() {
    const [query] = useSearchParams()
    const params = useMemo(() => parseConnectParams(query), [query])
    if ('error' in params) {
        return <Problem title="This link can't be used">{params.error}</Problem>
    }
    return <ConnectFlow params={params} />
}

function ConnectFlow({ params }: { params: ConnectParams }) {
    const location = useLocation()
    const here = `${location.pathname}${location.search}`
    const signedIn = useAppSelector(s => Boolean(s.auth.token && s.auth.user))
    const {
        data: req,
        isLoading,
        error,
    } = useGetConnectRequestQuery({
        platform: params.platform,
        store: params.store,
        returnUrl: params.returnUrl,
    })

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
            </Box>
        )
    }
    // The return URL is not trusted until the API accepted it: no redirect from here.
    if (error || !req) {
        return (
            <Problem title="This link can't be used">
                {v2ErrorMessage(error) ?? 'The app sent an invalid connect link.'}
            </Problem>
        )
    }

    const header = (
        <Box sx={{ ...column, gap: 1, textAlign: 'center' }}>
            <Typography variant="h5">Connect {req.platform.name} to YOM</Typography>
            <Typography color="text.secondary">
                <b>{req.store}</b> wants to send its analytics to your YOM account.
            </Typography>
        </Box>
    )

    if (!signedIn) {
        const next = encodeURIComponent(here)
        return (
            <Box sx={column}>
                {header}
                <Typography variant="body2" color="text.secondary" textAlign="center">
                    Sign in, or create a free account (no card needed), to continue.
                </Typography>
                <GoogleSignInButton text="continue_with" next={here} />
                <Button
                    variant="contained"
                    size="large"
                    component={Link}
                    to={`${DASHBOARD_AUTH_PATHS.login}?next=${next}`}
                >
                    Sign in with email
                </Button>
                <Button
                    variant="outlined"
                    size="large"
                    component={Link}
                    to={`${DASHBOARD_AUTH_PATHS.signup}?next=${next}`}
                >
                    Create a free account
                </Button>
                <Button color="inherit" href={cancelUrl(params)}>
                    Cancel
                </Button>
            </Box>
        )
    }
    return <Consent params={params} store={req.store} grants={req.grants} header={header} />
}

function Consent({
    params,
    store,
    grants,
    header,
}: {
    params: ConnectParams
    store: string
    grants: { type: string; scopes: string[]; description: string }[]
    header: React.ReactNode
}) {
    const { data: projects = [], isLoading } = useListProjectsQuery()
    const [createProject, { isLoading: creating, error: createError }] = useCreateProjectMutation()
    const [authorize, { isLoading: authorizing, error: authorizeError }] =
        useAuthorizeConnectMutation()
    const [picked, setPicked] = useState('')
    const [domain, setDomain] = useState(suggestedDomain(store))
    const [asClient, setAsClient] = useState(false)
    const [subTenant, setSubTenant] = useState(suggestedSubTenant(store))
    const [redirecting, setRedirecting] = useState(false)

    const projectId = picked || (projects.length === 0 ? NEW_PROJECT : (projects[0]?.id ?? ''))
    const isNew = projectId === NEW_PROJECT
    const subTenantOk = !asClient || isSubTenantId(subTenant)
    const busy = creating || authorizing || redirecting
    const failure =
        (createError && 'Could not create the project. Check the domain and your plan limits.') ||
        (authorizeError && (v2ErrorMessage(authorizeError) ?? 'That did not work. Try again.'))

    const connect = async () => {
        try {
            const id = isNew
                ? (await createProject({ name: store, domain }).unwrap()).id
                : projectId
            const res = await authorize({
                projectId: id,
                platform: params.platform,
                store: params.store,
                return: params.returnUrl,
                state: params.state,
                code_challenge: params.codeChallenge,
                code_challenge_method: 'S256',
                sub_tenant: asClient ? subTenant : null,
            }).unwrap()
            setRedirecting(true)
            window.location.assign(res.redirect_url)
        } catch {
            // shown below
        }
    }

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
            </Box>
        )
    }

    return (
        <Box sx={column}>
            {header}
            {failure && <Alert severity="error">{failure}</Alert>}
            <TextField
                select
                label="Project"
                value={projectId}
                onChange={e => setPicked(e.target.value)}
                fullWidth
            >
                {projects.map(p => (
                    <MenuItem key={p.id} value={p.id}>
                        {p.name}
                    </MenuItem>
                ))}
                <MenuItem value={NEW_PROJECT}>New project “{store}”</MenuItem>
            </TextField>
            {isNew && (
                <TextField
                    label="Website domain"
                    value={domain}
                    onChange={e => setDomain(e.target.value.trim().toLowerCase())}
                    required
                    fullWidth
                    helperText="The store's public domain, such as shop.example.com"
                />
            )}
            {!isNew && (
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={asClient}
                            onChange={e => setAsClient(e.target.checked)}
                        />
                    }
                    label="Agency or platform project: add this store as a client (sub-tenant)"
                />
            )}
            {!isNew && asClient && (
                <TextField
                    label="Client id (sub-tenant)"
                    value={subTenant}
                    onChange={e => setSubTenant(e.target.value)}
                    error={!subTenantOk}
                    helperText="Letters, digits and . _ : - (up to 64)"
                    fullWidth
                />
            )}
            <Box>
                <Typography variant="subtitle2">The app will get:</Typography>
                <List dense disablePadding>
                    {grants.map(g => (
                        <ListItem key={g.type} disableGutters>
                            <ListItemText
                                primary={g.description}
                                secondary={`Scopes: ${g.scopes.join(', ')}${asClient && !isNew ? ` · limited to client ${subTenant}` : ''}`}
                            />
                        </ListItem>
                    ))}
                </List>
                <Typography variant="caption" color="text.secondary">
                    Nothing else: it cannot manage your account, team or billing. You can revoke it
                    at any time in Settings → API keys; uninstalling the app revokes it too.
                </Typography>
            </Box>
            <Button
                variant="contained"
                size="large"
                disabled={busy || !subTenantOk || (isNew && !domain)}
                onClick={() => {
                    void connect()
                }}
            >
                {busy ? 'Connecting…' : 'Connect'}
            </Button>
            <Button color="inherit" href={cancelUrl(params)} disabled={busy}>
                Cancel
            </Button>
        </Box>
    )
}
